import { jsPDF } from "jspdf";
import { canv } from "@/canv/global";
import { getViewport, sortByIndexByParent } from "@/canvasController/helpers";
import type { Group } from "@/group/group";
import { selectionManager } from "@/tools/cursorTool";
import {
    addPdfPage,
    createPdfDoc,
    getDocSize,
    mapGroupsWithoutFrameToFrames,
    queryAreaForExport,
    savePDF,
} from "./utils";

export class BaseExport {
    async exportPage(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();
        const newCanvas = document.createElement("canvas");
        newCanvas.classList.add("exportCanvas");
        document.body.prepend(newCanvas);
        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) {
            newCanvas.remove();
            throw new Error("Failed to create canvas context for base PDF export");
        }

        const appCtx = canv.ctx;

        try {
            selectionManager.clearSelected();

            canv.zoomToSelected(groupsWithFrame, true);
            const areaWithFrames = canv.canvasController.getSelectedProps(groupsWithFrame);

            const visualWidth = (areaWithFrames.width / canv.dpr) * canv.m[0];
            const visualHeight = (areaWithFrames.height / canv.dpr) * canv.m[0];

            newCanvas.style.width = `${visualWidth}px`;
            newCanvas.style.height = `${visualHeight}px`;

            const rect = newCanvas.getBoundingClientRect();
            newCanvas.width = rect.width * canv.dpr;
            newCanvas.height = rect.height * canv.dpr;

            canv.ctx = newCtx;

            canv.ctx.setTransform(
                canv.m[0],
                0,
                0,
                canv.m[3],
                -areaWithFrames.startX * canv.m[0],
                -areaWithFrames.startY * canv.m[0],
            );

            // Получаем фигуры на фреймах
            const groupsByFrames = mapGroupsWithoutFrameToFrames(
                groupsWithFrame,
                groupsWithoutFrame,
            );
            const groupsForExport: Group[] = Object.values(groupsByFrames).reduce(
                (acc, cur) => {
                    acc.push(...cur);
                    return acc;
                },
                [...groupsWithFrame],
            );

            sortByIndexByParent(groupsForExport);

            canv.redraw();
            canv.redraw(true, true, groupsForExport);

            const doc = new jsPDF();
            await this.drawToDoc(newCanvas, visualWidth, visualHeight, doc);
            savePDF(doc);
        } finally {
            newCanvas.remove();
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        }
    }

    async exportPages(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();

        const groupsByFrames = mapGroupsWithoutFrameToFrames(groupsWithFrame, groupsWithoutFrame);

        const firstFrame = groupsWithFrame[0];
        const doc = createPdfDoc(firstFrame.width, firstFrame.height);
        const appCtx = canv.ctx;
        const newCanvas = document.createElement("canvas");
        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) {
            throw new Error("Failed to create canvas context for paged base PDF export");
        }

        document.body.prepend(newCanvas);

        let isFirstPage = true;

        try {
            selectionManager.clearSelected();

            for (const frameGroup of groupsWithFrame) {
                canv.zoomToSelected([frameGroup], true);

                const visualWidth = (frameGroup.width / canv.dpr) * canv.m[0];
                const visualHeight = (frameGroup.height / canv.dpr) * canv.m[0];

                newCanvas.style.width = `${visualWidth}px`;
                newCanvas.style.height = `${visualHeight}px`;

                const rect = newCanvas.getBoundingClientRect();
                newCanvas.width = rect.width * canv.dpr;
                newCanvas.height = rect.height * canv.dpr;

                canv.ctx = newCtx;

                canv.ctx.setTransform(
                    canv.m[0],
                    0,
                    0,
                    canv.m[3],
                    -frameGroup.startX * canv.m[0],
                    -frameGroup.startY * canv.m[0],
                );

                const groupsOnFrame = groupsByFrames[frameGroup.UID] || [];
                const groupsForExport = [frameGroup].concat(groupsOnFrame);
                sortByIndexByParent(groupsForExport);

                canv.redraw();
                canv.redraw(true, true, groupsForExport);

                await this.drawToDoc(newCanvas, visualWidth, visualHeight, doc, isFirstPage);
                this.clearCanvas(newCanvas, canv.ctx);

                isFirstPage = false;
            }

            savePDF(doc);
        } finally {
            newCanvas.remove();
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        }
    }

    async exportViewport() {
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();

        const newCanvas = document.createElement("canvas");
        newCanvas.classList.add("exportCanvas");
        document.body.prepend(newCanvas);

        const viewport = getViewport();

        const visualWidth = (viewport.viewportWidth / canv.dpr) * canv.m[0];
        const visualHeight = (viewport.viewportHeight / canv.dpr) * canv.m[0];

        newCanvas.style.width = `${visualWidth}px`;
        newCanvas.style.height = `${visualHeight}px`;

        const rect = newCanvas.getBoundingClientRect();
        newCanvas.width = rect.width * canv.dpr;
        newCanvas.height = rect.height * canv.dpr;

        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) {
            newCanvas.remove();
            throw new Error("Failed to create canvas context for viewport base PDF export");
        }

        const appCtx = canv.ctx;

        try {
            selectionManager.clearSelected();

            canv.ctx = newCtx;

            canv.ctx.setTransform(
                canv.m[0],
                0,
                0,
                canv.m[3],
                -viewport.viewportStartX * canv.m[0],
                -viewport.viewportStartY * canv.m[0],
            );

            const groupsForExport =
                queryAreaForExport({
                    startX: viewport.viewportStartX,
                    startY: viewport.viewportStartY,
                    width: viewport.viewportWidth,
                    height: viewport.viewportHeight,
                }) ?? [];

            sortByIndexByParent(groupsForExport);

            canv.redraw();
            canv.redraw(true, true, groupsForExport);

            const doc = new jsPDF();
            await this.drawToDoc(newCanvas, visualWidth, visualHeight, doc);
            savePDF(doc);
        } finally {
            newCanvas.remove();
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        }
    }

    private async drawToDoc(
        newCanvas: HTMLCanvasElement,
        visualWidth: number,
        visualHeight: number,
        doc: jsPDF,
        isFirstPage = true,
    ) {
        const snapshot = await new Promise<Blob>((resolve, reject) => {
            newCanvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error("Failed to create JPEG blob for base PDF export"));
                        return;
                    }

                    resolve(blob);
                },
                "image/jpeg",
                1,
            );
        });

        const blobUrl = URL.createObjectURL(snapshot);
        if (!isFirstPage) {
            addPdfPage(doc, visualWidth, visualHeight);
        }

        const { x, y, width, height } = getDocSize(doc, visualWidth, visualHeight);

        try {
            doc.addImage(blobUrl, "JPEG", x, y, width, height);
        } finally {
            URL.revokeObjectURL(blobUrl);
        }
    }

    private clearCanvas = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
    };
}
